import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Role } from "@/features/auth/types";
import {
  ROLE_OPTIONS,
  dateText,
  errText,
  fullName,
  platformApi,
  type AdminUser,
} from "@/admin/api";
import {
  FilterButtons,
  NativeSelect,
  Pager,
  Pill,
  SearchBox,
  TenantPicker,
} from "@/admin/components";

const FILTERS = [
  { value: "", label: "All" },
  { value: "superuser", label: "Superusers" },
  { value: "inactive", label: "Disabled" },
  { value: "locked", label: "Locked" },
  { value: "unverified", label: "Unverified" },
];

export function UserFlags({ u }: { u: AdminUser }) {
  return (
    <div className="flex flex-wrap gap-1">
      {u.is_superuser && <Pill tone="blue">Superuser</Pill>}
      {!u.is_active && <Pill tone="red">Disabled</Pill>}
      {u.locked && <Pill tone="amber">Locked</Pill>}
      {!u.email_verified && <Pill tone="gray">Unverified</Pill>}
      {u.is_active && !u.locked && !u.is_superuser && u.email_verified && <Pill tone="green">Active</Pill>}
    </div>
  );
}

export default function AdminUsersPage() {
  const [params, setParams] = useSearchParams();
  const tenant = params.get("tenant") ?? "";
  const tenantName = params.get("tenant_name") ?? "";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [only, setOnly] = useState("");
  const [role, setRole] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "users", page, search, only, role, tenant],
    queryFn: () =>
      platformApi.users({
        page,
        search: search || undefined,
        only: only || undefined,
        role: role || undefined,
        tenant: tenant || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">Every login on the platform, across all tenants.</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add user
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search name, email or tenant"
        />
        <NativeSelect
          value={role}
          onChange={(v) => {
            setRole(v);
            setPage(1);
          }}
          className="w-36"
        >
          <option value="">Any role</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </NativeSelect>
        <FilterButtons
          options={FILTERS}
          value={only}
          onChange={(v) => {
            setOnly(v);
            setPage(1);
          }}
        />
        {tenant && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setParams({});
              setPage(1);
            }}
          >
            Tenant: {tenantName || "selected"} <X className="ml-1 h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (q.data?.results ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No users match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Tenant</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data!.results.map((u) => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <Link to={`/admin/users/${u.id}`} className="font-medium hover:underline">
                          {fullName(u)}
                        </Link>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {u.tenant ? (
                          <Link to={`/admin/tenants/${u.tenant.id}`} className="hover:underline">
                            {u.tenant.name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 capitalize">{u.role}</td>
                      <td className="px-4 py-3">
                        <UserFlags u={u} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{dateText(u.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager data={q.data} onPage={setPage} />
        </CardContent>
      </Card>

      {addOpen && (
        <AddUserDialog
          defaultTenant={tenant ? { id: tenant, name: tenantName || "Selected tenant" } : null}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

function AddUserDialog({
  defaultTenant,
  onClose,
}: {
  defaultTenant: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tenant, setTenant] = useState(defaultTenant);
  const [role, setRole] = useState<Role>("member");
  const [superuser, setSuperuser] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      platformApi.createUser({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        tenant_id: tenant?.id ?? null,
        role,
        is_superuser: superuser,
      }),
    onSuccess: (u) => {
      toast.success(`User ${u.email} created.`);
      qc.invalidateQueries({ queryKey: ["admin"] });
      navigate(`/admin/users/${u.id}`);
    },
    onError: (e) => toast.error(errText(e, "Couldn't create the user.")),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>Their email is marked verified, so they can log in right away.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>First name</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Last name</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
          </div>
          <div className="space-y-1.5">
            <Label>Tenant</Label>
            <TenantPicker value={tenant} onChange={setTenant} allowNone />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <NativeSelect value={role} onChange={(v) => setRole(v as Role)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={superuser} onChange={(e) => setSuperuser(e.target.checked)} />
            Superuser (full platform admin access)
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
